import { useState } from 'react';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  NoSymbolIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { Avatar, Badge, Button, Modal, ModalFooter } from '../../../components';
import { useAuthStore } from '../../../stores';
import {
  useStaffList,
  useCreateStaff,
  useUpdateStaff,
  useDeleteStaff,
  useActivateStaff,
  useDeactivateStaff,
} from '../hooks/useStaff';
import { StaffMember, CreateStaffData, UpdateStaffData } from '../services/staffService';
import StaffFormModal from './StaffFormModal';
import { Spinner } from '../../../components';

type ConfirmAction = {
  type: 'delete' | 'activate' | 'deactivate';
  staff: StaffMember;
};

export default function StaffList() {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';

  const { data: staffList, isLoading } = useStaffList();
  const createStaff = useCreateStaff();
  const updateStaff = useUpdateStaff();
  const deleteStaff = useDeleteStaff();
  const activateStaff = useActivateStaff();
  const deactivateStaff = useDeactivateStaff();

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState<StaffMember | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);

  function handleAdd() {
    setEditingStaff(null);
    setShowFormModal(true);
  }

  function handleEdit(staff: StaffMember) {
    setEditingStaff(staff);
    setShowFormModal(true);
  }

  function handleFormSubmit(data: CreateStaffData | UpdateStaffData) {
    if (editingStaff) {
      updateStaff.mutate(
        { staffId: editingStaff.id, data: data as UpdateStaffData },
        { onSuccess: () => setShowFormModal(false) }
      );
    } else {
      createStaff.mutate(data as CreateStaffData, {
        onSuccess: () => setShowFormModal(false),
      });
    }
  }

  function handleConfirm() {
    if (!confirmAction) return;
    const { type, staff } = confirmAction;

    const onSuccess = () => setConfirmAction(null);

    if (type === 'delete') {
      deleteStaff.mutate(staff.id, { onSuccess });
    } else if (type === 'activate') {
      activateStaff.mutate(staff.id, { onSuccess });
    } else if (type === 'deactivate') {
      deactivateStaff.mutate(staff.id, { onSuccess });
    }
  }

  const confirmLoading =
    deleteStaff.isPending ||
    activateStaff.isPending ||
    deactivateStaff.isPending;

  function roleBadgeVariant(role: string) {
    switch (role) {
      case 'admin':
        return 'info' as const;
      case 'supervisor':
        return 'warning' as const;
      default:
        return 'default' as const;
    }
  }

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner size="lg" text="Loading staff..." />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink">Staff Members</h1>
          <p className="mt-1 text-sm text-ink-3">
            {staffList?.length || 0} member{staffList?.length !== 1 ? 's' : ''} in your team
          </p>
        </div>
        {isAdmin && (
          <Button onClick={handleAdd} leftIcon={<PlusIcon className="h-5 w-5" />}>
            Add Staff
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-card border border-line bg-surface shadow-s">
        <table className="min-w-full divide-y divide-line">
          <thead className="bg-surface-2">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-ink-3">
                Member
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-ink-3">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-ink-3">
                Department
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-ink-3">
                Status
              </th>
              {isAdmin && (
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-ink-3">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {staffList?.map((staff) => {
              const isSelf = staff.id === user?.id;
              return (
                <tr key={staff.id} className="hover:bg-black/[0.04] transition-colors">
                  {/* Member */}
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className="flex items-center gap-3">
                      <Avatar
                        name={staff.name}
                        src={staff.avatarUrl}
                        size="sm"
                        online={staff.online}
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-ink">
                            {staff.name}
                          </span>
                          {isSelf && (
                            <span className="text-xs text-ink-2">(you)</span>
                          )}
                        </div>
                        <div className="text-sm text-ink-3">{staff.email}</div>
                      </div>
                    </div>
                  </td>

                  {/* Role */}
                  <td className="whitespace-nowrap px-6 py-4">
                    <Badge variant={roleBadgeVariant(staff.role)} size="sm">
                      {staff.role.charAt(0).toUpperCase() + staff.role.slice(1)}
                    </Badge>
                  </td>

                  {/* Department */}
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-ink-3">
                    {staff.department || '—'}
                  </td>

                  {/* Status */}
                  <td className="whitespace-nowrap px-6 py-4">
                    {staff.active ? (
                      <Badge variant="success" size="sm">Active</Badge>
                    ) : (
                      <Badge variant="danger" size="sm">Inactive</Badge>
                    )}
                  </td>

                  {/* Actions */}
                  {isAdmin && (
                    <td className="whitespace-nowrap px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleEdit(staff)}
                          className="rounded-lg p-1.5 text-ink-2 hover:bg-black/[0.04] hover:text-ink"
                          title="Edit"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>

                        {!isSelf && staff.active && (
                          <button
                            onClick={() =>
                              setConfirmAction({ type: 'deactivate', staff })
                            }
                            className="rounded-lg p-1.5 text-ink-2 hover:bg-warning/10 hover:text-warning"
                            title="Deactivate"
                          >
                            <NoSymbolIcon className="h-4 w-4" />
                          </button>
                        )}

                        {!isSelf && !staff.active && (
                          <button
                            onClick={() =>
                              setConfirmAction({ type: 'activate', staff })
                            }
                            className="rounded-lg p-1.5 text-ink-2 hover:bg-success/10 hover:text-success"
                            title="Activate"
                          >
                            <CheckCircleIcon className="h-4 w-4" />
                          </button>
                        )}

                        {!isSelf && (
                          <button
                            onClick={() =>
                              setConfirmAction({ type: 'delete', staff })
                            }
                            className="rounded-lg p-1.5 text-ink-2 hover:bg-danger/10 hover:text-danger"
                            title="Delete"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}

            {(!staffList || staffList.length === 0) && (
              <tr>
                <td
                  colSpan={isAdmin ? 5 : 4}
                  className="px-6 py-12 text-center text-sm text-ink-3"
                >
                  No staff members found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      <StaffFormModal
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        onSubmit={handleFormSubmit}
        isLoading={createStaff.isPending || updateStaff.isPending}
        staff={editingStaff}
      />

      {/* Confirm Modal */}
      <Modal
        isOpen={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        title={
          confirmAction?.type === 'delete'
            ? 'Delete Staff Member'
            : confirmAction?.type === 'activate'
            ? 'Activate Staff Member'
            : 'Deactivate Staff Member'
        }
        size="sm"
      >
        <p className="text-sm text-ink-2">
          {confirmAction?.type === 'delete' && (
            <>
              Are you sure you want to permanently remove{' '}
              <strong>{confirmAction.staff.name}</strong>? This action cannot be
              undone.
            </>
          )}
          {confirmAction?.type === 'deactivate' && (
            <>
              Are you sure you want to deactivate{' '}
              <strong>{confirmAction.staff.name}</strong>? They will no longer be
              able to log in.
            </>
          )}
          {confirmAction?.type === 'activate' && (
            <>
              Activate <strong>{confirmAction?.staff.name}</strong>? They will be
              able to log in and handle conversations again.
            </>
          )}
        </p>
        <ModalFooter>
          <Button variant="outline" onClick={() => setConfirmAction(null)}>
            Cancel
          </Button>
          <Button
            variant={confirmAction?.type === 'delete' ? 'danger' : 'primary'}
            onClick={handleConfirm}
            isLoading={confirmLoading}
          >
            {confirmAction?.type === 'delete'
              ? 'Delete'
              : confirmAction?.type === 'activate'
              ? 'Activate'
              : 'Deactivate'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
